import * as Config from '@/Config';
import * as Manager from '@/Manager';
import * as Plugin from '@/Plugin';
import * as Provider from '@/App/Provider';
import ChalkySticks from '@chalkysticks/vue2-components';
import ChalkySticksPad from '@chalkysticks/vue2-pad';
import ChalkySticksSdk from '@chalkysticks/sdk';
import ChalkySticksTrainer from '@chalkysticks/vue2-trainer';
import FeaturePlugin from './Plugin/Feature';
import RegisterComponents from '@/Utility/RegisterComponents';
import Router from '@/Router';
import SettingPlugin from './Plugin/Setting';
import Store from '@/Store';
import Vue from 'vue';
import { assetRef } from '@/Manager/Asset';

/**
 * Register assets
 */
Manager.Asset.createIndex();

/**
 * Register Alert Manager
 */
Manager.Alert.install(Vue);

/**
 * Expose ChalkySticks and config to window for debugging and development purposes
 */
if (Config.Environment.IS_LOCAL) {
	(window as any).chalky = {
		app: {
			limit: 32,
			localUrl: Config.App.SERVER_URL,
		},
		headers: {
			csenv: Config.App.CS_HEADER,
		},
	};
}

/**
 * Disable console logs if Config.Environment.IS_PRODUCTION
 */
if (Config.Environment.IS_PRODUCTION) {
	console.error = () => {
		/* Do nothing */
	};
	console.info = () => {
		/* Do nothing */
	};
	console.log = () => {
		/* Do nothing */
	};
	console.warn = () => {
		/* Do nothing */
	};
}

/**
 * Disable Vue settings
 */
Vue.config.devtools = !Config.Environment.IS_PRODUCTION;
Vue.config.productionTip = false;
Vue.config.silent = true;

/**
 * Global event bus
 */
Vue.prototype.$bus = new Vue();

/**
 * Prototypes
 */
Vue.prototype.$asset = assetRef;

/**
 * Register components
 */
Vue.use(RegisterComponents, Config);

/**
 * Register Alert Plugin
 */
Vue.use(Plugin.Alert);

/**
 * Register Feature Plugin
 */
Vue.use(FeaturePlugin);

/**
 * Register Setting Plugin
 */
Vue.use(SettingPlugin);

/**
 * Register ChalkySticks components
 */
Vue.use(ChalkySticks, {
	app: {
		version: Config.App.VERSION,
	},
	google: {
		maps: {
			api_key: Config.App?.GOOGLE_MAP_API,
		},
	},
	provide: {
		router: Router,
		store: Store,
	},
	sdk: {
		api_url: Config.App?.SERVER_URL,
	},
});

/**
 * Configure ChalkySticks SDK
 */
ChalkySticksSdk.Core.ChalkySticks.configure({
	providers: [Provider.AppServiceProvider],
});

/**
 * Register ChalkySticks Pad components
 */
if (Config.App.USE_PAD) {
	Vue.use(ChalkySticksPad, {
		provide: {
			router: Router,
			store: Store,
		},
		sdk: {
			api_url: Config.App?.SERVER_URL,
		},
	});
}

/**
 * Register analytics if enabled
 */
if (Config.App.USE_ANALYTICS) {
	ChalkySticksSdk.Utility.Analytics.init({
		autoBind: Config.Analytics.AUTO_BIND,
		autoEmbed: Config.Analytics.AUTO_EMBED,
		context: {
			appVersion: Config.App.VERSION,
			env: Config.Environment.IS_LOCAL ? 'dev' : 'prod',
			user: Store.getters['authentication/user'] ? Store.getters['authentication/user'].id : 'guest',
		},
		defaultDebounce: Config.Analytics.DEFAULT_DEBOUNCE,
		defaultTTL: Config.Analytics.DEFAULT_TTL,
		privacy: {
			maskEmails: Config.Analytics.MASK_EMAILS,
			maskPhones: Config.Analytics.MASK_PHONES,
			scrubPII: Config.Analytics.SCRUB_PII,
		},
		providerOptions: {
			ga: {
				id: Config.Analytics.GOOGLE_ID,
			},
		},
		respectDNT: Config.Analytics.RESPECT_DNT,
		sampleRate: Config.Analytics.SAMPLE_RATE,
	});
}

/**
 * Disable all ChalkySticks SDK logs if not in local environment
 */
if (!Config.Environment.IS_LOCAL) {
	ChalkySticksSdk.Utility.Log.disableAll();
}

/**
 * Registers the service worker for Progressive Web App functionality
 */
if ('serviceWorker' in navigator) {
	window.addEventListener('load', (): void => {
		navigator.serviceWorker.register('/service-worker.js').then(
			(registration: ServiceWorkerRegistration): void => {
				// console.log('ServiceWorker registration successful:', registration);
			},
			(error: Error): void => {
				console.warn('ServiceWorker registration failed:', error);
			},
		);
	});
}
